/**
 * Group tree helpers. The tree has three levels:
 * 0 = Réseau, 1 = Communauté, 2 = Sous-communauté.
 * Every picker / filter / count must use these instead of mapping `children` once.
 */

export type TreeNode = {
  id: string;
  name: string;
  children?: TreeNode[] | null;
  [key: string]: any;
};

export type FlatGroup = {
  id: string;
  name: string;
  lvl: number;
  parentId?: string;
  parentName?: string;
  networkId: string;
  networkName: string;
  /* Names from the Réseau down to this group */
  path: string[];
  node: TreeNode;
  parentNode?: TreeNode;
};

/** Depth-first flattening of networks and all their descendants, with level and lineage. */
export const flattenGroupTree = (groups: TreeNode[]): FlatGroup[] => {
  const result: FlatGroup[] = [];
  const walk = (
    node: TreeNode,
    lvl: number,
    parent: TreeNode | undefined,
    network: TreeNode,
    path: string[]
  ) => {
    const entry: FlatGroup = {
      id: node.id,
      name: node.name,
      lvl,
      parentId: parent?.id,
      parentName: parent?.name,
      networkId: network.id,
      networkName: network.name,
      path: [...path, node.name],
      node,
      parentNode: parent,
    };
    result.push(entry);
    (node.children ?? []).forEach(child =>
      walk(child, lvl + 1, node, network, entry.path)
    );
  };
  groups.forEach(group => walk(group, 0, undefined, group, []));
  return result;
};

export const findGroupContext = (
  groups: TreeNode[],
  groupId: string
): FlatGroup | undefined =>
  flattenGroupTree(groups).find(group => group.id === groupId);

/** Number of descendants at any depth (communities + sub-communities). */
export const countDescendants = (node: {
  children?: Array<{ children?: any[] | null }> | null;
}): number =>
  (node.children ?? []).reduce(
    (count, child) => count + 1 + countDescendants(child),
    0
  );

/** "— " per level below the Réseau, for flat select options. */
export const indentLabel = (name: string, lvl: number) =>
  `${'— '.repeat(Math.max(lvl, 0))}${name}`;

/** Value format shared by the group tag fields: "name_id". */
export const groupTagValue = (group: { id: string; name: string }) =>
  `${group.name}_${group.id}`;
